/**
 * Header Component
 *
 * Main navigation header for public pages.
 * Inspired by 3D SCI design with transparent header over blue backgrounds.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogIn, LogOut } from 'lucide-react';
import { config } from '@/config';
import { features } from '@/config/features';
import { logout as authLogout } from '@/features/auth';
import { useAuthStore, selectIsAuthenticated, selectUser } from '@/store';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export function Header() {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const user = useAuthStore(selectUser);
  const clearUser = useAuthStore((state) => state.clearUser);
  const location = useLocation();
  const navigate = useNavigate();
  const isBlueHeader = location.pathname === '/' || location.pathname === '/browse';

  const handleLogout = async (): Promise<void> => {
    await authLogout();
    clearUser();
    void navigate('/');
  };

  const nameParts = user?.name?.trim().split(/\s+/).filter(Boolean) ?? [];
  const firstInitial = user?.firstName?.[0] ?? nameParts[0]?.[0] ?? user?.email?.[0] ?? '?';
  const lastInitial =
    user?.lastName?.[0] ?? (nameParts.length > 1 ? nameParts[nameParts.length - 1]?.[0] : '');
  const initials = (
    lastInitial ? `${firstInitial}${lastInitial}` : (user?.email?.slice(0, 2) ?? firstInitial)
  ).toUpperCase();

  return (
    <header
      className={cn(
        'fixed top-0 z-50 w-full border-b shadow-sm',
        isBlueHeader
          ? 'border-blue-800/30 bg-blue-700 text-white'
          : 'border-border bg-white text-foreground'
      )}
    >
      <div className="container mx-auto flex h-16 items-center px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link
          to="/"
          className={cn(
            'mr-3 flex shrink-0 items-center gap-2 text-base font-bold sm:mr-6 sm:text-lg',
            isBlueHeader ? 'text-white' : 'text-foreground'
          )}
        >
          <span
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg p-1',
              isBlueHeader ? 'bg-blue-700/50' : 'bg-blue-700'
            )}
          >
            <img
              src="/images/sci-modeling-logo.png"
              alt=""
              className="h-full w-full object-contain"
            />
          </span>
          <span>{config.appName}</span>
        </Link>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right side actions */}
        <div className="flex min-w-0 items-center space-x-1.5 sm:space-x-3">
          {features.auth && !isAuthenticated && (
            <>
              {config.auth.registrationEnabled && (
                <Button
                  asChild
                  size="sm"
                  className={cn(
                    'rounded-lg px-2 sm:px-3',
                    isBlueHeader && 'bg-white text-primary hover:bg-white/90'
                  )}
                >
                  <Link to="/register">Sign Up Free</Link>
                </Button>
              )}
              <Button
                asChild
                variant={isBlueHeader ? 'outline' : 'ghost'}
                size="sm"
                className={cn(
                  'gap-1 rounded-lg px-2 sm:gap-1.5 sm:px-3',
                  isBlueHeader && 'border-white/30 bg-white/10 text-white hover:bg-white/20'
                )}
              >
                <Link to="/login">
                  <LogIn className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Teacher Sign In</span>
                  <span className="sm:hidden">Sign In</span>
                </Link>
              </Button>
            </>
          )}

          {features.auth && isAuthenticated && user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={`Open account menu for ${user.name ?? user.email}`}
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full border text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2',
                    isBlueHeader
                      ? 'border-white/30 bg-white/15 text-white hover:bg-white/25 focus-visible:ring-white/80'
                      : 'border-primary/20 bg-primary/10 text-primary hover:bg-primary/20 focus-visible:ring-primary/50'
                  )}
                >
                  {initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <span className="block truncate">{user.name ?? 'Teacher'}</span>
                  <span className="block truncate text-xs font-normal text-muted-foreground">
                    {user.email}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/dashboard">Dashboard</Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => void handleLogout()}
                >
                  <LogOut />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
